import calendarIcon from "@tabler/icons/outline/calendar.svg";
import React from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { HStack, Icon, Stack, Text } from "@/components/ui";
import Signal from "@/features/ui/components/signal";
import { type PredictionStatus } from "@/schemas";
import { type Game } from "@/schemas/prediction";
import { cn } from "@/utils/cn";
import { shouldUseWhiteText } from "@/utils/colors";
import { formatTimeUntilEvent, getDateInLocalTimezone } from "@/utils/time";

const messages = defineMessages({
  beginsIn: { id: "prediction.begins_in", defaultMessage: "Begins in {time}" },
  startedAgo: {
    id: "prediction.started_ago",
    defaultMessage: "Started {time} ago",
  },
  badge_label: { id: "live_tv.badge_label", defaultMessage: "LIVE" },
});

function getLiveLabel(sportingEvent: Game): string | null {
  if (!sportingEvent.data.is_live) return null;
  if (sportingEvent.type === "mma") return sportingEvent.data.round;
  if (sportingEvent.type === "golf") return sportingEvent.data.round;
  return `${sportingEvent.data.period} • ${sportingEvent.data.time}`;
}

type PredictionHeaderProps = {
  status: PredictionStatus;
  isFocused?: boolean;
  isDetail?: boolean;
  isPanel?: boolean;
};

export default function PredictionHeader({
  status,
  isFocused,
  isDetail,
  isPanel = false,
}: PredictionHeaderProps) {
  const { prediction, sporting_event } = status;
  const { timeString, hasStarted } = formatTimeUntilEvent(
    prediction.event_date,
  );

  const isSportsLayout =
    !isDetail && ["Game", "Fight"].includes(prediction.type ?? "");

  if (isDetail && prediction.category === "Sports" && !isPanel) {
    const subcategoryLabel = [
      prediction.subcategory_display_name,
      prediction.venue,
    ]
      .filter(Boolean)
      .join(" • ");

    return (
      <Stack>
        <HStack space={3} alignItems="center">
          {prediction.thumbnail_static && (
            <img
              src={prediction.thumbnail_static}
              className="size-16 shrink-0 rounded-xl"
              alt={prediction.title}
            />
          )}

          <Stack space={1}>
            <Text weight="bold" size="2xl">
              {prediction.title}
            </Text>
            {!hasStarted && <EventTimeLabel timeString={timeString} />}
          </Stack>
        </HStack>

        <Stack space={6} className="mt-12">
          <HStack space={2} alignItems="center">
            <div className="h-px flex-1 bg-gray-300 dark:bg-gray-800" />
            {sporting_event?.data.is_live ? (
              <LiveIndicator sportingEvent={sporting_event} />
            ) : (
              <Text size="sm" weight="medium" theme="muted">
                {subcategoryLabel}
              </Text>
            )}
            <div className="h-px flex-1 bg-gray-300 dark:bg-gray-800" />
          </HStack>
          {sporting_event && <Scoreboard sportingEvent={sporting_event} />}
        </Stack>
      </Stack>
    );
  }

  if (isSportsLayout && !isPanel) {
    const localDate = getDateInLocalTimezone(new Date(prediction.event_date));
    const eventDate = `${localDate.hour}:${localDate.minute} ${localDate.timeZoneName} on ${localDate.month} ${localDate.day}`;
    const venueLabel = [prediction.venue, eventDate]
      .filter(Boolean)
      .join(" • ");

    return (
      <HStack space={3} alignItems="center">
        {sporting_event?.data.is_live ? (
          <LiveIndicator sportingEvent={sporting_event} />
        ) : (
          <Text theme="muted" size="sm" weight="medium" truncate>
            {venueLabel}
          </Text>
        )}
      </HStack>
    );
  }

  const titleSize = isDetail ? "2xl" : isFocused ? "xl" : "md";

  return (
    <HStack space={3} alignItems="center">
      {prediction.thumbnail_static && (
        <img
          src={prediction.thumbnail_static}
          className={cn("shrink-0", {
            "size-16 rounded-xl": isDetail || isFocused,
            "size-10 rounded-lg": !isDetail && !isFocused,
          })}
          alt={prediction.title}
        />
      )}

      <Stack className="w-full min-w-0">
        <Text size={titleSize} truncate={!isDetail} weight="bold" align="left">
          {prediction.title}
        </Text>
        {!hasStarted && <EventTimeLabel timeString={timeString} />}
      </Stack>
    </HStack>
  );
}

const EventTimeLabel = ({ timeString }: { timeString: string }) => (
  <HStack space={1} alignItems="center">
    <Icon src={calendarIcon} className="size-4 text-gray-600" />
    <Text size="sm" theme="muted">
      <FormattedMessage {...messages.beginsIn} values={{ time: timeString }} />
    </Text>
  </HStack>
);

const LiveIndicator = ({ sportingEvent }: { sportingEvent: Game }) => {
  const intl = useIntl();
  return (
    <HStack space={2} alignItems="center">
      <Signal />
      <Text size="sm" weight="medium" theme="secondary">
        {intl.formatMessage(messages.badge_label)}
      </Text>
      <Text size="sm" weight="medium" theme="secondary">
        {getLiveLabel(sportingEvent)}
      </Text>
    </HStack>
  );
};

const TeamBadge = ({
  color,
  abbreviation,
}: {
  color: string;
  abbreviation: string;
}) => (
  <div
    className="flex size-14 items-center justify-center rounded-lg"
    style={{ backgroundColor: `#${color}` }}
  >
    <Text
      size="md"
      weight="bold"
      className={cn({
        "text-gray-900": !shouldUseWhiteText(`#${color}`),
        "text-white": shouldUseWhiteText(`#${color}`),
      })}
    >
      {abbreviation.slice(0, 3)}
    </Text>
  </div>
);

const Scoreboard = ({ sportingEvent }: { sportingEvent: Game }) => {
  if (!("home_team" in sportingEvent.data)) return null;
  // TODO: still needed design for MMA and Golf

  const { home_team, away_team } = sportingEvent.data;

  return (
    <HStack justifyContent="center" alignItems="center" space={6}>
      <TeamBadge
        color={home_team.icon_colors[0].light_background}
        abbreviation={home_team.abbreviation}
      />
      <HStack justifyContent="center" alignItems="center" space={4}>
        <Text size="3xl" weight="bold">
          {home_team.score}
        </Text>
        <Text size="3xl" weight="bold">
          -
        </Text>
        <Text size="3xl" weight="bold">
          {away_team.score}
        </Text>
      </HStack>
      <TeamBadge
        color={away_team.icon_colors[0].light_background}
        abbreviation={away_team.abbreviation}
      />
    </HStack>
  );
};
